// Which primary destinations an org's portal shows.
//
// Orgs rolling out a single product (e.g. access reviews only) can strip the
// portal back to just what their people need — the selection is configured per
// org in the dashboard's Portal settings and arrives on the portal config as
// `navItems`.
//
// Kept free of React/icon imports so the rules stay unit-testable on their own.

export interface NavVisibilityItem {
  id: string;
}

/**
 * Narrows `items` to the ids the org enabled.
 *
 * Fails open — an absent selection (config still loading, request failed, or an
 * org whose row predates the setting) and a selection naming only ids this
 * build doesn't know both fall back to the full nav. A member is never left
 * with no way off the page they're on, and the cost of failing open is a
 * briefly-wider nav rather than a stuck portal.
 */
export const filterNavItems = <T extends NavVisibilityItem>(
  items: T[],
  enabledIds: string[] | null | undefined,
): T[] => {
  if (!enabledIds) return items;

  const visible = items.filter((item) => enabledIds.includes(item.id));
  return visible.length > 0 ? visible : items;
};

/**
 * Whether the header shows its "Request access" button.
 *
 * The button is the portal's one always-on-screen way into the catalog, so an
 * org that isn't ready for its people to raise requests can switch it off
 * independently of the nav (PLO-5751). Fails open for the same reason the nav
 * does: an absent value is a config still loading, a request that failed, or a
 * row predating the setting, none of which should quietly remove the button.
 *
 * Hiding it is cosmetic, not access control — the catalog routes stay mounted
 * and a direct link still works.
 */
export const isRequestAccessVisible = (
  enabled: boolean | null | undefined,
): boolean => enabled !== false;

/**
 * Where `/members` should send a member when Home itself is hidden.
 *
 * Home is the portal's post-login landing route, so hiding it without this
 * would drop reviewers straight onto the one page their org took out of the
 * nav. Returns `undefined` while Home is visible — i.e. no redirect.
 */
export const hiddenHomeRedirectPath = <T extends NavVisibilityItem & { to: string }>(
  items: T[],
  enabledIds: string[] | null | undefined,
): string | undefined => {
  const visible = filterNavItems(items, enabledIds);
  if (visible.some((item) => item.id === "home")) return undefined;

  return visible[0]?.to;
};

/**
 * The ids of the destinations the nav actually renders for this org.
 *
 * Derived from `filterNavItems` rather than the raw selection so anything else
 * keyed on nav visibility (Home's cards) cannot disagree with the nav itself.
 * An unknown selection restores the full nav, and this restores the full id set
 * with it.
 */
export const visibleNavIds = <T extends NavVisibilityItem>(
  items: T[],
  enabledIds: string[] | null | undefined,
): ReadonlySet<string> =>
  new Set(filterNavItems(items, enabledIds).map((item) => item.id));
